import { useCallback, useRef, useState, useEffect } from "react";

/**
 * useFileDropzone — minimal drag-and-drop file hook (desktop-only).
 *
 * Returns refs + handler bag to spread on the drop-target element, plus an
 * `isOver` flag for visual feedback. Disabled on touch devices (pointer:
 * coarse) so phones/tablets don't get phantom highlights.
 *
 * @param onFiles ({File[]}) => void — called when files are dropped
 * @param accept string[] of allowed extensions (case-insensitive, with or
 *               without leading dot). If omitted, accepts anything.
 * @param multiple boolean — if false, only the first file is forwarded
 * @param disabled boolean
 */
export default function useFileDropzone({ onFiles, accept = null, multiple = true, disabled = false }) {
  const [isOver, setIsOver] = useState(false);
  const enterCount = useRef(0);

  // desktop = devices with a fine pointer (mouse / trackpad)
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(pointer: fine)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: fine)");
    const cb = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", cb);
    else mq.addListener(cb);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", cb);
      else mq.removeListener(cb);
    };
  }, []);

  const allowed = useCallback((file) => {
    if (!accept || !accept.length) return true;
    const name = (file.name || "").toLowerCase();
    return accept.some((ext) => {
      const e = ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
      return name.endsWith(e);
    });
  }, [accept]);

  const handlers = {
    onDragEnter: (e) => {
      if (disabled || !isDesktop) return;
      // Only react to actual file drags (skip text drags etc)
      if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
      e.preventDefault();
      enterCount.current += 1;
      setIsOver(true);
    },
    onDragOver: (e) => {
      if (disabled || !isDesktop) return;
      if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e) => {
      if (disabled || !isDesktop) return;
      e.preventDefault();
      enterCount.current = Math.max(0, enterCount.current - 1);
      if (enterCount.current === 0) setIsOver(false);
    },
    onDrop: (e) => {
      if (disabled || !isDesktop) return;
      e.preventDefault();
      enterCount.current = 0;
      setIsOver(false);
      const files = Array.from(e.dataTransfer?.files || []).filter(allowed);
      if (!files.length) return;
      onFiles(multiple ? files : [files[0]]);
    },
  };

  return { handlers, isOver, isDesktop };
}
